import React, { useState, useEffect, useCallback } from 'react';
import { Layout, usePrivacy } from '../components/Layout';
import { statsApi, quotasApi } from '../services/api';
import { ArrowRightLeft, Clock, CheckCircle2, XCircle, TrendingUp, Coins, RefreshCw, Plus, Zap, FileCheck, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const PERIODS = [
  { value: '', label: 'Tout' },
  { value: 'week', label: 'Semaine' },
  { value: 'month', label: 'Mois' },
  { value: 'year', label: 'Annee' },
];

const DashboardPage = () => {
  const { admin } = useAuth();
  const { hidden } = usePrivacy();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('');
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try { const r = await statsApi.get(period ? { period } : {}); setStats(r.data); } catch {}
    setLoading(false);
  }, [period]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const mask = (val) => hidden ? '••••••' : val;
  const formatAmount = (a, c = 'EUR') => hidden ? `•••••• ${c}` : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: c }).format(a || 0);
  const formatDate = (d) => { try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }); } catch { return d; } };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Bonjour';
    if (h < 18) return 'Bon apres-midi';
    return 'Bonsoir';
  };

  const v1 = stats?.status_breakdown || {};
  const v2 = stats?.v2?.status_breakdown || {};
  const ch = stats?.cheques?.status_breakdown || {};
  const totalAll = (stats?.total_transfers || 0) + (stats?.v2?.total || 0) + (stats?.cheques?.total || 0);
  const pendingAll = (v1.pending?.count || 0) + (v2.pending?.count || 0) + (ch.pending?.count || 0);
  const executedAll = (v1.executed?.count || 0) + (v2.executed?.count || 0) + (ch.executed?.count || 0);
  const rejectedAll = (v1.rejected?.count || 0) + (v2.rejected?.count || 0) + (ch.rejected?.count || 0);
  const totalAmountAll = (stats?.total_amount || 0) + (stats?.v2?.total_amount || 0) + (stats?.cheques?.total_amount || 0);
  const totalFeesAll = (stats?.total_fees || 0) + (stats?.v2?.total_fees || 0);

  const pieData = [
    { name: 'En attente', value: pendingAll, color: '#FBBF24' },
    { name: 'Execute', value: executedAll, color: '#34D399' },
    { name: 'Rejete', value: rejectedAll, color: '#F87171' },
  ].filter(d => d.value > 0);

  const statusBadge = (s) => {
    const cfg = { pending: 'bg-[#FEF08A] text-[#854D0E]', executed: 'bg-[#DCFCE7] text-[#166534]', rejected: 'bg-[#FEE2E2] text-[#991B1B]' };
    const lbl = { pending: 'En attente', executed: 'Execute', rejected: 'Rejete' };
    return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg[s] || ''}`}>{lbl[s] || s}</span>;
  };

  if (loading && !stats) return <Layout><div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Greeting */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-950 tracking-tight">{getGreeting()} {admin?.name?.split(' ')[0] || ''} !</h1>
          <p className="text-sm text-slate-500 mt-1">Voici vos statistiques d'aujourd'hui sur WorldPay.</p>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate('/transfers')} className="bg-slate-950 hover:bg-slate-800 text-white font-medium rounded-full px-5 py-2.5 text-sm flex items-center gap-2 transition-all shadow-sm" data-testid="quick-new-transfer">
            <Plus className="w-4 h-4" />Ajouter un virement
          </button>
          <button onClick={() => navigate('/invoices')} className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium rounded-full px-5 py-2.5 text-sm flex items-center gap-2 transition-all" data-testid="quick-invoice">
            <FileText className="w-4 h-4" />Creer une facture
          </button>
        </div>

        {/* Period Filter */}
        <div className="flex items-center gap-1.5" data-testid="period-filter">
          {PERIODS.map(p => (
            <button key={p.value} onClick={() => setPeriod(p.value)} data-testid={`period-${p.value || 'all'}`}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                period === p.value ? 'bg-slate-950 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${period === p.value ? 'bg-white' : 'bg-slate-400'}`} />
              {p.label}
            </button>
          ))}
        </div>

        {/* Main KPI */}
        <div className="bg-[#F3F4F6] rounded-3xl p-6" data-testid="kpi-main">
          <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-slate-500" /></div>
          <p className="text-3xl sm:text-4xl font-bold text-slate-950 tracking-tight">{formatAmount(totalAmountAll)}</p>
          <p className="text-sm text-slate-500 mt-1">Revenu total</p>
        </div>

        {/* Secondary KPIs */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#F3F4F6] rounded-2xl p-5">
            <Coins className="w-4 h-4 text-slate-400 mb-2" />
            <p className="text-xl sm:text-2xl font-bold text-slate-950">{formatAmount(totalFeesAll)}</p>
            <p className="text-xs text-slate-500 mt-1">Total frais</p>
          </div>
          <div className="bg-[#F3F4F6] rounded-2xl p-5">
            <ArrowRightLeft className="w-4 h-4 text-slate-400 mb-2" />
            <p className="text-xl sm:text-2xl font-bold text-slate-950">{mask(totalAll)}</p>
            <p className="text-xs text-slate-500 mt-1">Operations totales</p>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-amber-500" /><span className="text-xs font-medium text-slate-500">En attente</span></div>
            <p className="text-2xl font-bold text-slate-950">{mask(pendingAll)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span className="text-xs font-medium text-slate-500">Executes</span></div>
            <p className="text-2xl font-bold text-slate-950">{mask(executedAll)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-slate-200">
            <div className="flex items-center gap-2 mb-2"><XCircle className="w-4 h-4 text-red-500" /><span className="text-xs font-medium text-slate-500">Rejetes</span></div>
            <p className="text-2xl font-bold text-slate-950">{mask(rejectedAll)}</p>
          </div>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-200" data-testid="module-v1">
            <div className="flex items-center gap-2 mb-3"><ArrowRightLeft className="w-4 h-4 text-slate-500" /><span className="text-sm font-semibold text-slate-900">Virements V1</span></div>
            <p className="text-xl font-bold text-slate-950">{mask(stats?.total_transfers || 0)}</p>
            <p className="text-xs text-slate-500 mt-1">{formatAmount(stats?.total_amount)}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-slate-200" data-testid="module-v2">
            <div className="flex items-center gap-2 mb-3"><Zap className="w-4 h-4 text-[#F5C518]" /><span className="text-sm font-semibold text-slate-900">Virements V2</span><span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-[#F5C518] text-slate-900">PRO</span></div>
            <p className="text-xl font-bold text-slate-950">{mask(stats?.v2?.total || 0)}</p>
            <p className="text-xs text-slate-500 mt-1">{formatAmount(stats?.v2?.total_amount)}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-slate-200" data-testid="module-cheques">
            <div className="flex items-center gap-2 mb-3"><FileCheck className="w-4 h-4 text-emerald-500" /><span className="text-sm font-semibold text-slate-900">Cheques</span><span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-[#F5C518] text-slate-900">PRO</span></div>
            <p className="text-xl font-bold text-slate-950">{mask(stats?.cheques?.total || 0)}</p>
            <p className="text-xs text-slate-500 mt-1">{formatAmount(stats?.cheques?.total_amount)}</p>
          </div>
        </div>

        {/* Chart */}
        {pieData.length > 0 && (
          <div className="bg-white rounded-2xl p-5 border border-slate-200">
            <p className="text-sm font-semibold text-slate-900 mb-4">Repartition</p>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={62} paddingAngle={3} dataKey="value" strokeWidth={0}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie><Tooltip formatter={(v) => [v, 'Operations']} contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0', fontSize: '12px' }} /></PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-5 mt-3">
              {pieData.map(item => <div key={item.name} className="flex items-center gap-1.5 text-xs"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} /><span className="text-slate-500">{item.name}</span></div>)}
            </div>
          </div>
        )}

        {/* Recent */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-900">Virements recents</p>
            <button onClick={() => navigate('/transfers')} className="text-xs font-medium text-slate-500 hover:text-slate-900 border border-slate-200 rounded-full px-3 py-1" data-testid="view-all-transfers-btn">Voir tout</button>
          </div>
          {stats?.recent_transfers?.length > 0 ? stats.recent_transfers.map((t) => (
            <div key={t.id} className="flex items-center justify-between px-5 py-3.5 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate('/transfers')}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{hidden ? '••••••' : t.beneficiary_name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{formatDate(t.created_at)}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-3">
                <span className="text-sm font-bold text-slate-900">{formatAmount(t.amount, t.currency)}</span>
                {statusBadge(t.status)}
              </div>
            </div>
          )) : <div className="p-8 text-center text-sm text-slate-400">Aucun virement recent</div>}
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
